import { COMPANY_PDF_LOGO } from "@/lib/brand";
import { formatDate, formatNumber } from "@/lib/format";

// Mirrors the printable shape used by the old BillForge desktop app so the
// rendered output sits side-by-side with the Quotation PDF and reuses the
// same html2canvas → jsPDF capture pipeline (see web/src/lib/htmlPdf.ts).
export interface SalesInvoicePrintItem {
  description: string;
  itemType: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
}

export interface SalesInvoicePrintData {
  number: string;
  invoiceDate?: string | number | null;
  dueDate?: string | number | null;
  status?: string;
  quotationRef?: string;
  customerName: string;
  customerPhone?: string;
  customerAddress?: string;
  items: SalesInvoicePrintItem[];
  subtotal: number;
  discountPercent: number;
  discountAmount: number;
  // When false (deployment's `features.tax` flag OFF on a freshly-built
  // invoice) every tax row is suppressed. Defaults to true/undefined so saved
  // invoices that were billed WITH tax keep rendering their historical levies
  // (they are still gated by amount > 0, so a tax-free invoice shows nothing).
  showTax?: boolean;
  taxable: number;
  nhilRate: number;
  nhil: number;
  getfundRate: number;
  getfund: number;
  vatRate: number;
  vat: number;
  transportation?: number;
  installationRate?: number;
  installationAmount?: number;
  netTotal: number;
  total: number;
  notes?: string;
  terms?: string;
}

export interface SalesInvoicePrintCompany {
  name: string;
  address: string;
  phone: string;
  email: string;
  currency: string;
}

interface Props {
  invoice: SalesInvoicePrintData;
  company: SalesInvoicePrintCompany;
  preparedBy?: string;
}

export default function SalesInvoicePrint({
  invoice,
  company,
  preparedBy = "System",
}: Props) {
  const terms: string = String(invoice.terms || "");
  const discountAmt = Number(invoice.discountAmount || 0);
  const showDiscount = discountAmt > 0 || Number(invoice.discountPercent || 0) > 0;
  const taxAllowed = invoice.showTax !== false;
  const showNhil = taxAllowed && Number(invoice.nhil || 0) > 0;
  const showGetfund = taxAllowed && Number(invoice.getfund || 0) > 0;
  const showVat = taxAllowed && Number(invoice.vat || 0) > 0;
  const showTaxable = Number(invoice.taxable || 0) > 0 && (showNhil || showGetfund || showVat);
  const transportation = Number(invoice.transportation || 0);
  const installationAmt = Number(invoice.installationAmount || 0);
  const installationRate = Number(invoice.installationRate || 0);
  const hasOptional = transportation > 0 || installationAmt > 0;
  const showNetTotal =
    Number(invoice.netTotal || 0) > 0 &&
    Number(invoice.netTotal || 0) !== Number(invoice.total || 0);

  return (
    <div
      className="print-area relative bg-white text-slate-900 p-5 font-sans overflow-hidden"
      style={{ width: "800px" }}
    >
      <div className="relative z-10">
        <div className="flex justify-between items-start border-b-2 border-slate-300 pb-2">
          <div className="flex items-center gap-4">
            <img
              src={COMPANY_PDF_LOGO}
              alt="Company logo"
              className="h-20 w-auto object-contain"
            />
            <div>
              <div className="text-lg font-bold uppercase leading-tight">
                {company.name}
              </div>
              <div className="text-[11px] mt-0.5">{company.address}</div>
              <div className="text-[11px]">Tel: {company.phone}</div>
              <div className="text-[11px]">Email: {company.email}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xl font-bold tracking-wide leading-tight">
              SALES INVOICE
            </div>
            <div className="text-sm font-mono mt-0.5">{invoice.number}</div>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-[13px] leading-tight">
          <div>
            <div className="text-[10px] uppercase text-slate-500">Bill To</div>
            <div className="font-semibold mt-0.5">{invoice.customerName || "—"}</div>
            {invoice.customerPhone ? (
              <div className="text-[11px] text-slate-600 mt-0.5">
                Tel: {invoice.customerPhone}
              </div>
            ) : null}
            {invoice.customerAddress ? (
              <div className="text-[11px] text-slate-600 mt-0.5 whitespace-pre-wrap">
                {invoice.customerAddress}
              </div>
            ) : null}
          </div>
          <div className="text-right space-y-0.5">
            <div>
              <span className="text-[10px] uppercase text-slate-500">
                Invoice Date:{" "}
              </span>
              <span className="font-semibold">
                {invoice.invoiceDate ? formatDate(invoice.invoiceDate) : "—"}
              </span>
            </div>
            {invoice.quotationRef ? (
              <div>
                <span className="text-[10px] uppercase text-slate-500">
                  Quotation Ref:{" "}
                </span>
                <span className="font-semibold font-mono">
                  {invoice.quotationRef}
                </span>
              </div>
            ) : null}
          </div>
        </div>

        <table className="w-full mt-3 text-[11px] border-collapse">
          <thead>
            <tr className="bg-slate-100">
              <th className="border border-slate-300 px-2 py-1.5 text-center w-8">
                #
              </th>
              <th className="border border-slate-300 px-2 py-1.5 text-left">
                Description
              </th>
              <th className="border border-slate-300 px-2 py-1.5 text-left">
                Unit
              </th>
              <th className="border border-slate-300 px-2 py-1.5 text-right">
                Quantity
              </th>
              <th className="border border-slate-300 px-2 py-1.5 text-right">
                Unit Price ({company.currency})
              </th>
              <th className="border border-slate-300 px-2 py-1.5 text-right">
                Line Total ({company.currency})
              </th>
            </tr>
          </thead>
          <tbody>
            {invoice.items.map((it, i) => (
              <tr key={i}>
                <td className="border border-slate-300 px-2 py-1.5 text-center align-middle">
                  {i + 1}
                </td>
                <td className="border border-slate-300 px-2 py-1.5 align-middle">
                  <div>{it.description}</div>
                </td>
                <td className="border border-slate-300 px-2 py-1.5 align-middle">
                  {it.itemType}
                </td>
                <td className="border border-slate-300 px-2 py-1.5 text-right font-mono align-middle">
                  {formatNumber(it.quantity)}
                </td>
                <td className="border border-slate-300 px-2 py-1.5 text-right font-mono align-middle">
                  {formatNumber(it.unitPrice)}
                </td>
                <td className="border border-slate-300 px-2 py-1.5 text-right font-mono align-middle">
                  {formatNumber(it.lineTotal)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-2 flex justify-end">
          <table className="text-[11px] w-72">
            <tbody>
              <Row label="Subtotal" value={invoice.subtotal} />
              {showDiscount && (
                <Row
                  label={`Discount (${invoice.discountPercent}%)`}
                  value={discountAmt}
                />
              )}
              {showTaxable && (
                <Row label="Taxable Amount" value={invoice.taxable} />
              )}
              {showNhil && (
                <Row
                  label={`NHIL (${invoice.nhilRate}%)`}
                  value={invoice.nhil}
                />
              )}
              {showGetfund && (
                <Row
                  label={`GETFund (${invoice.getfundRate}%)`}
                  value={invoice.getfund}
                />
              )}
              {showVat && (
                <Row
                  label={`VAT (${invoice.vatRate}%)`}
                  value={invoice.vat}
                />
              )}
              {showNetTotal && (
                <tr className="border-t border-slate-300">
                  <td className="py-0.5 font-semibold">NET TOTAL</td>
                  <td className="py-0.5 text-right font-mono font-semibold">
                    {formatNumber(invoice.netTotal)}
                  </td>
                </tr>
              )}
              {hasOptional && (
                <tr>
                  <td
                    colSpan={2}
                    className="pt-1 text-[10px] uppercase tracking-wider text-slate-500"
                  >
                    Optional Services
                  </td>
                </tr>
              )}
              {transportation > 0 && (
                <Row label="Transportation" value={transportation} />
              )}
              {installationAmt > 0 && (
                <Row
                  label={`Installation${
                    installationRate > 0 ? ` (${installationRate}%)` : ""
                  }`}
                  value={installationAmt}
                />
              )}
              <tr className="border-t-2 border-slate-400">
                <td className="py-1 font-bold">Total ({company.currency})</td>
                <td className="py-1 text-right font-bold text-sm">
                  {formatNumber(invoice.total)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-3 pt-2 border-t border-slate-200 text-xs">
          <div>Thank you for your business!</div>
          {invoice.notes && invoice.notes.trim() ? (
            <div className="mt-1 text-[10px] text-slate-600">
              Notes: {invoice.notes}
            </div>
          ) : null}
          {terms.trim() && (
            <div className="mt-2 pt-2 border-t border-slate-200">
              <div className="text-[10px] uppercase font-semibold text-slate-700 mb-0.5">
                Terms &amp; Conditions
              </div>
              <div className="text-[9px] text-slate-600 whitespace-pre-wrap leading-snug">
                {terms}
              </div>
            </div>
          )}
          <div className="mt-3 pt-2 border-t border-slate-200 flex items-end justify-between">
            <div className="text-[11px]">
              <div className="text-slate-500 uppercase tracking-wide">
                Prepared By
              </div>
              <div className="font-semibold mt-0.5">{preparedBy}</div>
            </div>
            <div className="text-[11px] text-right min-w-[220px]">
              <div className="text-slate-500 uppercase tracking-wide">
                Customer Signature
              </div>
              <div className="mt-4 border-b border-slate-400 w-full" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: number }) {
  return (
    <tr>
      <td className="py-0.5 text-slate-600">{label}</td>
      <td className="py-0.5 text-right font-mono">{formatNumber(value)}</td>
    </tr>
  );
}
